import { useState } from 'react';
import { flushSync } from 'react-dom';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AddIcon,
  Box,
  ChevronRightIcon,
  Divider,
  KeyboardDoubleArrowLeftIcon,
  KeyboardDoubleArrowRightIcon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Text,
  Tooltip,
} from '@/ui/primitives';
import { Stack } from '../layout/Stack';
import {
  buildSidebarCollapseControlSx,
  buildSidebarCollapseIconSx,
  buildSidebarFooterItemStackSx,
  buildSidebarItemIconSx,
  buildSidebarItemLabelSx,
  buildSidebarItemRootSx,
  buildSidebarItemStackSx,
  buildSidebarPopoverAddActionItemSx,
  buildSidebarPopoverMenuItemSx,
  buildSidebarPopoverPaperSxForTheme,
  buildSidebarPopoverTitleTextSx,
  buildSidebarSectionFrameSx,
  leadingAdornmentSpacing,
  sidebarCollapseLabelSx,
  sidebarFooterDividerFrameSx,
  sidebarFooterFrameSx,
  sidebarFooterTitleSx,
  sidebarItemContentSx,
  sidebarItemFontSize,
  sidebarItemLeadingAdornmentFrameSx,
  sidebarItemTrailingContentSx,
  sidebarNavRootSx,
  sidebarPopoverItemHelperTextSx,
  sidebarPopoverItemPrimaryTextSx,
  sidebarPopoverItemTextFrameSx,
  sidebarPopoverLeadingAdornmentFrameSx,
  sidebarPopoverMenuListSx,
  sidebarPopoverTitleFrameSx,
  sidebarSectionLabelSx,
} from './SidebarNav.styles';
import { SidebarCollapsedNotificationIcon, SidebarNotificationBadge } from './SidebarNavBadges';
import type { ReactElement, ReactNode } from 'react';

export type SidebarItem = {
  id: string;
  label: string;
  href: string;
  icon?: ReactElement;
  leadingAdornment?: ReactElement;
  active?: boolean;
  disabled?: boolean;
  disabledTone?: 'muted' | 'default';
  preload?: () => Promise<void> | null;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  popover?: {
    title?: string;
    items: Array<{
      id: string;
      label: string;
      onClick?: () => void;
      leadingAdornment?: ReactElement;
      helperText?: string;
    }>;
  };
  rightAdornment?: ReactElement;
  notificationCount?: number;
};

export type SidebarSection = { title: string; items: SidebarItem[] };

type SidebarNavProps = {
  sections: SidebarSection[];
  preFooterItems?: SidebarItem[];
  footerItems?: SidebarItem[];
  footerTitle?: string;
  footerContent?: ReactNode;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onBeforeNavigate?: (href: string, label: string) => void;
};

export const SidebarNav = ({
  sections,
  preFooterItems,
  footerItems,
  footerTitle,
  footerContent,
  collapsed = false,
  onToggleCollapsed,
  onBeforeNavigate,
}: SidebarNavProps): ReactElement => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activePopover, setActivePopover] = useState<SidebarItem['popover'] | null>(null);
  const resolvedPreFooterItems = preFooterItems ?? [];
  const resolvedFooterItems = footerItems ?? [];
  const hasPreFooterItems = resolvedPreFooterItems.length > 0;
  const hasFooterItems = resolvedFooterItems.length > 0;

  const scheduleNavigationAfterLoadingPaint = (href: string): void => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        void navigate(href);
      });
    });
  };

  const handleClose = (): void => {
    setAnchorEl(null);
    setActivePopover(null);
  };

  const renderSidebarItem = (item: SidebarItem): ReactElement => {
    const notificationCount = item.notificationCount ?? 0;
    const hasNotificationCount = notificationCount > 0;
    const notificationLabel = notificationCount > 99 ? '99+' : String(notificationCount);
    const collapsedAriaLabel =
      collapsed && hasNotificationCount ? `${item.label}, new items available` : undefined;
    const icon =
      collapsed && item.icon && hasNotificationCount ? (
        <SidebarCollapsedNotificationIcon icon={item.icon} />
      ) : (
        item.icon
      );
    const notificationBadge = hasNotificationCount ? (
      <SidebarNotificationBadge itemLabel={item.label} notificationLabel={notificationLabel} />
    ) : null;
    const itemNode = (
      <Box
        key={item.id}
        component={item.popover ? 'a' : RouterLink}
        {...(item.popover ? { href: item.href } : { to: item.href })}
        aria-label={collapsedAriaLabel}
        aria-disabled={item.disabled ? true : undefined}
        data-has-popover={item.popover ? 'true' : undefined}
        onMouseEnter={() => {
          if (!item.disabled) {
            void item.preload?.();
          }
        }}
        onFocus={() => {
          if (!item.disabled) {
            void item.preload?.();
          }
        }}
        onMouseDown={(event) => {
          if (item.disabled || item.active || item.popover || !item.href.startsWith('/')) {
            return;
          }
          const isPrimaryButton = event.button === 0;
          const isModifiedClick = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
          if (!isPrimaryButton || isModifiedClick) {
            return;
          }
          flushSync(() => {
            onBeforeNavigate?.(item.href, item.label);
          });
        }}
        onClick={(event) => {
          if (item.disabled) {
            event.preventDefault();
            return;
          }
          if (!item.popover && item.href.startsWith('/')) {
            const isPrimaryButton = event.button === 0;
            const isModifiedClick =
              event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
            if (isPrimaryButton && !isModifiedClick) {
              event.preventDefault();
              flushSync(() => {
                onBeforeNavigate?.(item.href, item.label);
              });
              void item.preload?.()?.catch(() => null);
              scheduleNavigationAfterLoadingPaint(item.href);
              return;
            }
          }
          item.onClick?.(event);
          if (item.popover) {
            event.preventDefault();
            setAnchorEl(event.currentTarget);
            setActivePopover(item.popover);
          }
        }}
        aria-current={item.active ? 'page' : undefined}
        sx={buildSidebarItemRootSx({
          active: item.active === true,
          collapsed,
          disabled: item.disabled === true,
          disabledTone: item.disabledTone,
        })}
      >
        {item.leadingAdornment && !collapsed ? (
          <Box sx={sidebarItemLeadingAdornmentFrameSx}>{item.leadingAdornment}</Box>
        ) : null}
        {icon ? (
          <ListItemIcon
            sx={buildSidebarItemIconSx({
              active: item.active === true,
              collapsed,
              disabled: item.disabled === true,
              disabledTone: item.disabledTone,
            })}
          >
            {icon}
          </ListItemIcon>
        ) : null}
        {collapsed ? null : (
          <Box sx={sidebarItemContentSx}>
            <Text role="body" component="span" sx={buildSidebarItemLabelSx(item.active === true)}>
              {item.label}
            </Text>
            {notificationBadge}
          </Box>
        )}
        {!collapsed && (item.popover || item.rightAdornment) ? (
          <Box sx={sidebarItemTrailingContentSx}>
            {item.rightAdornment}
            {item.popover ? <ChevronRightIcon fontSize="small" /> : null}
          </Box>
        ) : null}
      </Box>
    );

    return collapsed ? (
      <Tooltip key={item.id} title={item.label} placement="right" arrow>
        {itemNode}
      </Tooltip>
    ) : (
      itemNode
    );
  };

  const renderCollapseControl = (): ReactElement | null => {
    if (!onToggleCollapsed) return null;
    const label = collapsed ? 'Expand Sidebar' : 'Collapse Sidebar';
    const icon = collapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />;
    const control = (
      <Box
        component="button"
        type="button"
        aria-label={label}
        onClick={onToggleCollapsed}
        sx={buildSidebarCollapseControlSx(collapsed)}
      >
        <ListItemIcon sx={buildSidebarCollapseIconSx(collapsed)}>{icon}</ListItemIcon>
        {collapsed ? null : (
          <Text role="body" component="span" sx={sidebarCollapseLabelSx}>
            Collapse
          </Text>
        )}
      </Box>
    );
    return collapsed ? (
      <Tooltip title={label} placement="right" arrow>
        {control}
      </Tooltip>
    ) : (
      control
    );
  };

  return (
    <Box component="nav" aria-label="Primary" sx={sidebarNavRootSx}>
      {sections.map((section, index) => (
        <Box key={`${section.title}-${String(index)}`} sx={buildSidebarSectionFrameSx(index)}>
          {section.title && !collapsed ? (
            <Text role="label" component="div" sx={sidebarSectionLabelSx}>
              {section.title}
            </Text>
          ) : null}
          <Stack gap="sm" sx={buildSidebarItemStackSx(collapsed)}>
            {section.items.map(renderSidebarItem)}
          </Stack>
        </Box>
      ))}
      {footerTitle && !collapsed ? (
        <Text role="label" sx={sidebarFooterTitleSx}>
          {footerTitle}
        </Text>
      ) : null}
      {footerContent}
      <Box sx={sidebarFooterFrameSx}>
        {hasPreFooterItems ? (
          <Stack
            gap="sm"
            sx={buildSidebarFooterItemStackSx({ collapsed, hasFollowingGroup: hasFooterItems })}
          >
            {resolvedPreFooterItems.map(renderSidebarItem)}
          </Stack>
        ) : null}
        {hasFooterItems ? (
          <Stack gap="sm" sx={buildSidebarFooterItemStackSx({ collapsed })}>
            {collapsed ? null : (
              <Text role="label" component="div" sx={sidebarSectionLabelSx}>
                Settings
              </Text>
            )}
            {resolvedFooterItems.map(renderSidebarItem)}
          </Stack>
        ) : null}
        {hasPreFooterItems || hasFooterItems ? (
          <Stack gap="sm" sx={buildSidebarFooterItemStackSx({ collapsed })}>
            <Box sx={sidebarFooterDividerFrameSx}>
              <Divider />
            </Box>
            {renderCollapseControl()}
          </Stack>
        ) : (
          <Stack gap="sm" sx={buildSidebarFooterItemStackSx({ collapsed })}>
            {renderCollapseControl()}
          </Stack>
        )}
      </Box>
      <Popover
        open={Boolean(anchorEl && activePopover)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        paperSx={buildSidebarPopoverPaperSxForTheme(sidebarItemFontSize)}
      >
        {(() => {
          const addActionItem =
            activePopover?.items.find((item) => item.id.startsWith('add-')) ?? null;
          const selectableItems =
            activePopover?.items.filter((item) => !item.id.startsWith('add-')) ?? [];
          const allProjectsItem = selectableItems.find((item) => item.id === 'all-projects');
          const otherSelectableItems = selectableItems.filter((item) => item.id !== 'all-projects');
          const orderedSelectableItems = allProjectsItem
            ? [allProjectsItem, ...otherSelectableItems]
            : otherSelectableItems;

          return (
            <>
              {activePopover?.title ? (
                <>
                  <Box sx={sidebarPopoverTitleFrameSx}>
                    <Text role="body" sx={buildSidebarPopoverTitleTextSx(sidebarItemFontSize)}>
                      {activePopover.title}
                    </Text>
                  </Box>
                  <Divider />
                </>
              ) : null}
              <MenuList dense disablePadding sx={sidebarPopoverMenuListSx}>
                {orderedSelectableItems.map((popoverItem) => (
                  <MenuItem
                    key={popoverItem.id}
                    onClick={() => {
                      popoverItem.onClick?.();
                      handleClose();
                    }}
                    sx={(theme) =>
                      buildSidebarPopoverMenuItemSx({
                        itemLeadingAdornmentSpacing: leadingAdornmentSpacing,
                        textColor: theme.app.colors.text,
                      })
                    }
                  >
                    {popoverItem.leadingAdornment ? (
                      <Box
                        data-sidebar-popover-leading-adornment="true"
                        sx={sidebarPopoverLeadingAdornmentFrameSx}
                      >
                        {popoverItem.leadingAdornment}
                      </Box>
                    ) : null}
                    <Box sx={sidebarPopoverItemTextFrameSx}>
                      <Text role="body" sx={sidebarPopoverItemPrimaryTextSx}>
                        {popoverItem.label}
                      </Text>
                      {popoverItem.helperText ? (
                        <Text
                          role="caption"
                          color="textSecondary"
                          sx={sidebarPopoverItemHelperTextSx}
                        >
                          {popoverItem.helperText}
                        </Text>
                      ) : null}
                    </Box>
                  </MenuItem>
                ))}
                {addActionItem ? (
                  <Tooltip
                    title={addActionItem.onClick ? '' : 'Project Limit Reached'}
                    placement="right"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: { fontSize: (theme) => theme.typography.body.fontSize },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        if (!addActionItem.onClick) return;
                        addActionItem.onClick();
                        handleClose();
                      }}
                      sx={(theme) =>
                        buildSidebarPopoverAddActionItemSx({
                          enabled: Boolean(addActionItem.onClick),
                          textColor: theme.app.colors.text,
                          textSecondaryColor: theme.palette.text.secondary,
                        })
                      }
                    >
                      <ListItemIcon>
                        <AddIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={addActionItem.label} />
                    </MenuItem>
                  </Tooltip>
                ) : null}
              </MenuList>
            </>
          );
        })()}
      </Popover>
    </Box>
  );
};
